import React, { useEffect, useState } from 'react';
import SlidePanel from '../shared/SlidePanel';
import { sitesService, siteColour } from '../../_services/sites.service';
import { productService } from '../../_services';

// Same response as checkout's product picker: the API answers either with a
// flat product list or with categories carrying nested `products`. Flatten
// both so the dropdown is never empty for a salon that has stock.
function unwrapProducts(res) {
  const d = res && res.data ? (res.data.data !== undefined ? res.data.data : (res.data.responseData !== undefined ? res.data.responseData : res.data)) : res;
  let list = [];
  if (Array.isArray(d)) {
    list = d.some(x => x && Array.isArray(x.products)) ? d.reduce((acc, cat) => acc.concat(cat.products || []), []) : d;
  } else if (d) {
    list = d.products || d.productList || d.list || d.result || [];
    if (Array.isArray(list) && list.some(x => x && Array.isArray(x.products))) list = list.reduce((acc, cat) => acc.concat(cat.products || []), []);
  }
  return (Array.isArray(list) ? list : []).filter(p => p && p._id).map(p => ({ _id: p._id, name: p.productName || p.name || 'Product', quantity: p.quantity, trackStock: p.trackStock, variants: p.variants || [] }));
}
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const fmtDateTime = (d) => new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const PAGE = 20;
const STATUS_LABEL = { sent: 'In transit', received: 'Received', cancelled: 'Cancelled' };

/**
 * Stock transfers between locations — filterable, paginated history with a
 * detail sheet per transfer, plus slide-in panels to send and receive.
 */
export default function StockTransfers({ sites, active }) {
  const [list, setList] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState({ direction: 'all', status: '', q: '' });
  const [products, setProducts] = useState([]);
  const [sending, setSending] = useState(false);
  const [receiving, setReceiving] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [msg, setMsg] = useState(null);
  const mySite = active ? String(active.salonId) : localStorage.getItem('salonId');

  const load = (reset = true) => {
    const skip = reset ? 0 : (list || []).length;
    if (!reset) setLoadingMore(true);
    return sitesService.stockTransfers({ direction: filters.direction, status: filters.status || undefined, q: filters.q || undefined, skip, limit: PAGE })
      .then((d) => {
        const rows = (d && d.transfers) || [];
        setList(reset ? rows : (list || []).concat(rows));
        setTotal(d && d.total != null ? d.total : rows.length);
        setHasMore(!!(d && d.hasMore));
      })
      .catch(() => { if (reset) setList([]); })
      .finally(() => setLoadingMore(false));
  };
  useEffect(() => {
    productService.getProductsList({ salonId: localStorage.getItem('salonId') || '', search: '' }).then(r => setProducts(unwrapProducts(r))).catch(() => setProducts([]));
  }, []);
  useEffect(() => { setList(null); load(true); }, [filters.direction, filters.status, filters.q]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async (t, onDone) => {
    try { await sitesService.cancelStockTransfer(t._id); setMsg(`${t.reference || 'Transfer'} cancelled. Stock is back here.`); if (onDone) onDone(); load(true); }
    catch (e) { setMsg((e.response && e.response.data && e.response.data.message) || e.message || 'Could not cancel'); }
  };

  return (
    <>
      <div className="v2-toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="v2-ed-chips" style={{ margin: 0 }}>
          {[['all', 'All'], ['in', 'Incoming'], ['out', 'Outgoing']].map(([v, l]) => (
            <button key={v} type="button" className={`v2-ed-chip ${filters.direction === v ? '' : 'v2-ed-chip--add'}`} onClick={() => setFilters({ ...filters, direction: v })}>{l}</button>
          ))}
        </div>
        <select className="sp-input" style={{ width: 150 }} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} aria-label="Status">
          <option value="">Any status</option><option value="sent">In transit</option><option value="received">Received</option><option value="cancelled">Cancelled</option>
        </select>
        <input className="sp-input" style={{ width: 200 }} placeholder="Search reference or item" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} aria-label="Search transfers" />
        <div className="v2-toolbar-spacer" />
        <button type="button" className="v2-btn-primary" onClick={() => setSending(true)}>Send stock</button>
      </div>
      {msg && <p className="v2-pp-help" style={{ margin: '0 0 10px' }}>{msg}</p>}
      {!list ? <div className="v2-page-stub"><p className="v2-page-stub-sub">Loading…</p></div> : !list.length ? (
        <div className="v2-page-stub">
          <p className="v2-page-stub-title">{filters.q || filters.status || filters.direction !== 'all' ? 'No transfers match' : 'No transfers yet'}</p>
          <p className="v2-page-stub-sub">Products match by SKU, barcode, then name at the destination. Stock leaves here when you send it and arrives when the other location receives it.</p>
        </div>
      ) : (
        <>
          <div className="v2-table-wrap"><table className="v2-table">
            <thead><tr><th>Transfer</th><th>Items</th><th>Status</th><th>Sent</th><th aria-hidden /></tr></thead>
            <tbody>{list.map(t => {
              const inbound = String(t.toSalonId) === mySite;
              const disc = t.lines.filter(l => l.discrepancy).length;
              const units = t.lines.reduce((n, l) => n + (l.qty || 0), 0);
              return (
                <tr key={t._id} className="v2-row-click" style={{ cursor: 'pointer' }} onClick={() => setDetailId(t._id)}>
                  <td><div className="v2-cell-title"><span className="ml-dot" style={{ background: siteColour(inbound ? t.fromSalonId : t.toSalonId) }} />{inbound ? `From ${t.fromSalonName}` : `To ${t.toSalonName}`}</div><div className="v2-cell-sub">{t.reference || ''}{t.createdByName ? ` · ${t.createdByName}` : ''}</div></td>
                  <td><div className="v2-cell-sub">{t.lines.length} line{t.lines.length === 1 ? '' : 's'} · {units} unit{units === 1 ? '' : 's'}{disc ? ` · ${disc} short` : ''}</div></td>
                  <td><span className={`v2-status ${t.status === 'received' ? 'v2-status--active' : t.status === 'cancelled' ? 'v2-status--off' : 'v2-status--draft'}`}>{STATUS_LABEL[t.status] || t.status}</span></td>
                  <td><div className="v2-cell-sub">{fmtDate(t.createdTime)}</div></td>
                  <td className="v2-td-chev" onClick={(e) => e.stopPropagation()}>
                    {t.status === 'sent' && inbound && <button type="button" className="sp-btn-primary" onClick={() => setReceiving({ transfer: t, received: {} })}>Receive</button>}
                    {t.status === 'sent' && !inbound && <button type="button" className="sp-btn-secondary" onClick={() => setDetailId(t._id)}>Cancel…</button>}
                  </td>
                </tr>
              );
            })}</tbody>
          </table></div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 2px' }}>
            <span className="v2-cell-sub">Showing {list.length} of {total}</span>
            {hasMore && <button type="button" className="sp-btn-secondary" onClick={() => load(false)} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'Load more'}</button>}
          </div>
        </>
      )}
      {sending && <SendPanel sites={sites} active={active} products={products} onClose={() => setSending(false)} onSent={(m) => { setMsg(m); setSending(false); load(true); }} />}
      {receiving && <ReceivePanel state={receiving} setState={setReceiving} onDone={(m) => { setMsg(m); setReceiving(null); load(true); }} />}
      {detailId && <DetailPanel id={detailId} mySite={mySite} onClose={() => setDetailId(null)} onReceive={(t) => { setDetailId(null); setReceiving({ transfer: t, received: {} }); }} onCancel={(t) => cancel(t, () => setDetailId(null))} />}
    </>
  );
}

/** One transfer: header, lines with received / short, people and timeline, notes, actions. */
function DetailPanel({ id, mySite, onClose, onReceive, onCancel }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  useEffect(() => {
    let alive = true;
    sitesService.stockTransfer(id).then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setError((e.response && e.response.data && e.response.data.message) || e.message || 'Could not load'); });
    return () => { alive = false; };
  }, [id]);
  const t = data && data.transfer;
  const inbound = t && String(t.toSalonId) === mySite;
  const money = (n) => `£${(Number(n) || 0).toFixed(2)}`;
  const footer = t && t.status === 'sent' ? (
    inbound
      ? <><button type="button" className="sp-btn-secondary" onClick={onClose}>Close</button><button type="button" className="sp-btn-primary" onClick={() => onReceive(t)}>Receive</button></>
      : confirmCancel
        ? <><button type="button" className="sp-btn-secondary" onClick={() => setConfirmCancel(false)}>Keep it</button><button type="button" className="sp-btn-primary" style={{ background: '#B14438' }} onClick={() => onCancel(t)}>Cancel transfer</button></>
        : <><button type="button" className="sp-btn-secondary" onClick={onClose}>Close</button><button type="button" className="sp-btn-secondary" onClick={() => setConfirmCancel(true)}>Cancel transfer…</button></>
  ) : <button type="button" className="sp-btn-secondary" onClick={onClose}>Close</button>;
  return (
    <SlidePanel title={t ? (t.reference || 'Transfer') : 'Transfer'} onClose={onClose} width={560} className="voucher-v2 v2-editor-panel" footer={footer}>
      {error && <p className="v2-ed-error">{error}</p>}
      {!t && !error && <p className="v2-pp-help">Loading…</p>}
      {t && (
        <>
          <div className="v2-pp-section">
            <div className="v2-ed-row" style={{ alignItems: 'center', gap: 10 }}>
              <span className="ml-dot" style={{ background: siteColour(t.fromSalonId) }} /><strong>{t.fromSalonName}</strong>
              <span style={{ color: '#8E8E93' }}>→</span>
              <span className="ml-dot" style={{ background: siteColour(t.toSalonId) }} /><strong>{t.toSalonName}</strong>
              <span className={`v2-status ${t.status === 'received' ? 'v2-status--active' : t.status === 'cancelled' ? 'v2-status--off' : 'v2-status--draft'}`} style={{ marginLeft: 'auto' }}>{STATUS_LABEL[t.status] || t.status}</span>
            </div>
            {confirmCancel && <p className="v2-pp-help" style={{ color: '#B14438' }}>Cancelling returns every line to {t.fromSalonName}. The other location is told.</p>}
          </div>
          <div className="v2-pp-section">
            <div className="v2-pp-section-header"><p className="beu-section-label">Items</p></div>
            <div className="v2-table-wrap"><table className="v2-table">
              <thead><tr><th>Item</th><th>Sent</th><th>Received</th><th>Short</th>{t.status === 'received' && <th>Cost short</th>}</tr></thead>
              <tbody>{t.lines.map((l) => (
                <tr key={l._id}>
                  <td><div className="v2-cell-title">{l.label}</div></td>
                  <td><div className="v2-cell-sub">{l.qty}</div></td>
                  <td><div className="v2-cell-sub">{l.receivedQty != null ? l.receivedQty : (t.status === 'sent' ? 'Not yet' : '')}</div></td>
                  <td><div className="v2-cell-sub" style={l.discrepancy ? { color: '#B14438', fontWeight: 600 } : undefined}>{l.discrepancy ? l.discrepancy : (l.receivedQty != null ? 0 : 'Not yet')}</div></td>
                  {t.status === 'received' && <td><div className="v2-cell-sub">{l.discrepancy ? money((l.discrepancy || 0) * (l.unitCost || 0)) : ''}</div></td>}
                </tr>
              ))}</tbody>
              {data.totals && <tfoot><tr><td><strong>Total</strong></td><td>{data.totals.sent}</td><td>{t.status === 'received' ? data.totals.received : 'Not yet'}</td><td>{t.status === 'received' ? data.totals.short : 'Not yet'}</td>{t.status === 'received' && <td>{data.totals.short ? money(data.totals.shortCost) : ''}</td>}</tr></tfoot>}
            </table></div>
          </div>
          <div className="v2-pp-section">
            <div className="v2-pp-section-header"><p className="beu-section-label">Timeline</p></div>
            {(data.timeline || []).map((ev, i) => (
              <div key={i} className="v2-ed-row" style={{ justifyContent: 'space-between', padding: '6px 0', borderTop: i ? '1px solid #F2F2F7' : 'none' }}>
                <span style={{ fontSize: 13.5 }}>{ev.event === 'sent' ? 'Sent' : ev.event === 'received' ? 'Received' : 'Cancelled'}{ev.by ? ` by ${ev.by}` : ''}</span>
                <span className="v2-cell-sub">{fmtDateTime(ev.at)}</span>
              </div>
            ))}
            {t.notes ? <p className="v2-pp-help" style={{ marginTop: 10 }}><strong>Notes:</strong> {t.notes}</p> : null}
          </div>
        </>
      )}
    </SlidePanel>
  );
}

function SendPanel({ sites, active, products, onClose, onSent }) {
  const others = (sites || []).filter(s => !s.isActive);
  // One other location = it is the destination; nothing to choose.
  const [form, setForm] = useState({ toSalonId: others.length === 1 ? String(others[0].salonId) : '', lines: [], notes: '' });
  const [picker, setPicker] = useState({ productId: '', variantId: '', qty: 1 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const product = products.find(p => p._id === picker.productId);
  const addLine = () => {
    if (!picker.productId || !(picker.qty > 0)) return;
    if (product && product.variants.length && !picker.variantId) return;
    const v = product && product.variants.find(x => x._id === picker.variantId);
    setForm(f => ({ ...f, lines: f.lines.concat({ productId: picker.productId, variantId: picker.variantId || undefined, qty: Number(picker.qty), label: (product ? product.name : '') + (v ? ' · ' + Object.values(v.optionValues || {}).join(' / ') : '') }) }));
    setPicker({ productId: '', variantId: '', qty: 1 });
  };
  const send = async () => {
    setBusy(true); setError(null);
    try {
      const r = await sitesService.createStockTransfer({ toSalonId: form.toSalonId, lines: form.lines, notes: form.notes });
      onSent(r.unmatched && r.unmatched.length ? `Sent. ${r.unmatched.length} line(s) skipped: ${r.unmatched.map(u => `${u.productName || ''} (${u.reason})`).join('; ')}` : 'Transfer sent.');
    } catch (e) {
      const un = (e && e.data && e.data.unmatched) || [];
      setError(un.length ? `Nothing sent: ${un.map(u => `${u.productName || ''} (${u.reason})`).join('; ')}` : ((e.response && e.response.data && e.response.data.message) || e.message || 'Could not send'));
    }
    finally { setBusy(false); }
  };
  return (
    <SlidePanel title={`Send stock from ${active ? active.salonName : 'here'}`} onClose={onClose} width={520} className="voucher-v2 v2-editor-panel"
      footer={<><button type="button" className="sp-btn-secondary" onClick={onClose}>Cancel</button><button type="button" className="sp-btn-primary" onClick={send} disabled={busy || !form.toSalonId || !form.lines.length}>{busy ? 'Sending…' : 'Send transfer'}</button></>}>
      <div className="v2-pp-section">
        <div className="v2-ed-field"><label className="v2-ed-label">To</label>
          <div className="v2-ed-chips">{others.map(s => <button key={String(s.salonId)} type="button" className={`v2-ed-chip ${form.toSalonId === String(s.salonId) ? '' : 'v2-ed-chip--add'}`} onClick={() => setForm({ ...form, toSalonId: String(s.salonId) })}><span className="ml-dot" style={{ background: siteColour(s.salonId) }} />{s.salonName}</button>)}</div>
        </div>
      </div>
      <div className="v2-pp-section">
        <div className="v2-pp-section-header"><p className="beu-section-label">Items</p></div>
        <div className="v2-ed-field"><label className="v2-ed-label">Product</label>
          <select className="sp-input" value={picker.productId} onChange={(e) => setPicker({ ...picker, productId: e.target.value, variantId: '' })}><option value="">Choose a product</option>{products.map(p => <option key={p._id} value={p._id}>{p.name}{p.trackStock === 1 ? ` (${p.quantity} in stock)` : ''}</option>)}</select>
        </div>
        {product && product.variants.length > 0 && <div className="v2-ed-field"><label className="v2-ed-label">Variant</label><select className="sp-input" value={picker.variantId} onChange={(e) => setPicker({ ...picker, variantId: e.target.value })}><option value="">Choose</option>{product.variants.map(v => <option key={v._id} value={v._id}>{Object.values(v.optionValues || {}).join(' / ')} ({v.quantity})</option>)}</select></div>}
        <div className="v2-ed-row" style={{ alignItems: 'flex-end' }}>
          <div className="v2-ed-field"><label className="v2-ed-label">Quantity</label><input type="number" className="sp-input" min="1" value={picker.qty} onChange={(e) => setPicker({ ...picker, qty: e.target.value })} /></div>
          <div className="v2-ed-field"><button type="button" className="sp-btn-secondary" onClick={addLine}>Add line</button></div>
        </div>
        {form.lines.length > 0 && <div className="v2-ed-chips">{form.lines.map((l, i) => <button key={i} type="button" className="v2-ed-chip" onClick={() => setForm(f => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }))}>{l.label} × {l.qty}<span className="v2-ed-chip-x">×</span></button>)}</div>}
        <div className="v2-ed-field" style={{ marginTop: 12 }}><label className="v2-ed-label">Notes</label><input type="text" className="sp-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        {error && <p className="v2-ed-error">{error}</p>}
      </div>
    </SlidePanel>
  );
}

function ReceivePanel({ state, setState, onDone }) {
  const t = state.transfer;
  const [busy, setBusy] = useState(false);
  const receive = async () => {
    setBusy(true);
    try {
      const r = await sitesService.receiveStockTransfer(t._id, t.lines.map(l => ({ lineId: l._id, receivedQty: state.received[l._id] !== undefined ? Number(state.received[l._id]) : l.qty })));
      onDone(r.discrepancies.length ? `Received with ${r.discrepancies.length} line(s) short, £${Number(r.missingCost).toFixed(2)} missing at cost.` : 'Received in full.');
    } catch (e) { onDone((e.response && e.response.data && e.response.data.message) || 'Could not receive'); }
    finally { setBusy(false); }
  };
  return (
    <SlidePanel title={`Receive ${t.reference}`} onClose={() => setState(null)} width={520} className="voucher-v2 v2-editor-panel"
      footer={<><button type="button" className="sp-btn-secondary" onClick={() => setState(null)}>Cancel</button><button type="button" className="sp-btn-primary" onClick={receive} disabled={busy}>{busy ? 'Receiving…' : 'Confirm receipt'}</button></>}>
      <div className="v2-pp-section">
        <p className="v2-pp-help">Enter what actually arrived. Anything short is recorded as a discrepancy.</p>
        <div className="v2-table-wrap"><table className="v2-table">
          <thead><tr><th>Item</th><th>Sent</th><th>Received</th></tr></thead>
          <tbody>{t.lines.map(l => (
            <tr key={l._id}><td><div className="v2-cell-title">{l.label}</div></td><td><div className="v2-cell-sub">{l.qty}</div></td>
              <td><input type="number" className="sp-input" style={{ width: 90 }} min="0" max={l.qty} value={state.received[l._id] !== undefined ? state.received[l._id] : l.qty} onChange={(e) => setState({ ...state, received: { ...state.received, [l._id]: e.target.value } })} /></td></tr>
          ))}</tbody>
        </table></div>
      </div>
    </SlidePanel>
  );
}
